import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes } from "react-router-dom";
import { useMe } from "../App";
import { api, useApi } from "../lib/api";
import { Badge, Button, Card, cx, Empty, ErrorNote, Field, fmtDate, inputCls, Loading, Modal, Table, Td, useAction } from "../components/ui";
import { BounceRate, CapBar } from "./Stats";
import Learning from "./Learning";

// Settings grouped by job (Phase 5.6): Sending, Content, Data, System.
const GROUPS: [string, [string, string][]][] = [
  ["Sending", [["sending", "Kill switch & jobs"], ["mailboxes", "Mailboxes"], ["calendar", "Holidays & send window"], ["template", "Sequence template"]]],
  ["Content", [["rules", "Writing rules"], ["learning", "Learning"], ["kb", "Knowledge base"]]],
  ["Data", [["import", "Import"], ["territories", "Territories"], ["suppression", "Suppression list"], ["guardrails", "Guardrails & offerings"]]],
  ["System", [["users", "Users"], ["ai", "AI"], ["simulate", "Simulate inbound"], ["audit", "Audit log"]]],
];

export default function Settings() {
  const { me } = useMe();
  if (!me.isAdmin) return <ErrorNote error="Settings are for admins." />;
  return (
    <div className="space-y-5">
      <h1 className="text-[1.45rem] font-semibold tracking-tight">Settings</h1>
      <div className="grid gap-6 lg:grid-cols-[200px_minmax(0,1fr)]">
        <nav className="space-y-4" aria-label="Settings sections">
          {GROUPS.map(([group, tabs]) => (
            <div key={group}>
              <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted">{group}</div>
              {tabs.map(([k, l]) => (
                <NavLink key={k} to={`/admin/settings/${k}`} className={({ isActive }) => cx("block rounded-lg px-3 py-1.5 text-sm", isActive ? "bg-accent-soft font-medium text-accent-text" : "text-ink-2 hover:bg-surface-2")}>
                  {l}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="min-w-0 space-y-4">
          <Routes>
            <Route index element={<Navigate to="sending" replace />} />
            <Route path="sending" element={<Sending />} />
            <Route path="template" element={<Template />} />
            <Route path="rules" element={<Rules />} />
            <Route path="learning" element={<Learning />} />
            <Route path="kb" element={<Kb />} />
            <Route path="suppression" element={<Suppression />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="mailboxes" element={<Mailboxes />} />
            <Route path="guardrails" element={<Guardrails />} />
            <Route path="ai" element={<Ai />} />
            <Route path="users" element={<Users />} />
            <Route path="territories" element={<Territories />} />
            <Route path="import" element={<Import />} />
            <Route path="simulate" element={<Simulate />} />
            <Route path="audit" element={<Audit />} />
            <Route path="*" element={<Navigate to="sending" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function useSettings() {
  return useApi<any>("/settings");
}
const Saved = ({ show }: { show: boolean }) => (show ? <span className="text-sm text-good">Saved</span> : null);

function Sending() {
  const { refreshMe } = useMe();
  const { data, reload } = useSettings();
  const act = useAction();
  const [confirm, setConfirm] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [reason, setReason] = useState("");
  const { data: ready } = useApi<any[]>(releasing ? "/settings/readiness" : null, [releasing]);
  const [results, setResults] = useState<Record<string, any>>({});
  if (!data) return <Loading />;
  const on = data.settings.killSwitch;
  const run = (job: string) => act.run(async () => { const r = await api("/jobs/run", { body: { job } }); setResults((x) => ({ ...x, [job]: r })); });
  return (
    <div className="space-y-4">
      <Card title="Kill switch">
        <div className="flex flex-wrap items-center gap-4">
          <div className={cx("rounded-full px-3 py-1 text-sm font-semibold", on ? "bg-bad text-white" : "bg-good-soft text-good")}>{on ? "Live sending paused (test and dry-run continue)" : "Live sending allowed"}</div>
          {on ? (
            <Button tone="primary" onClick={() => { setReason(""); setReleasing(true); }}>Resume sending…</Button>
          ) : (
            <Button tone="danger" className="px-5 py-3 text-base" onClick={() => setConfirm(true)}>Activate kill switch</Button>
          )}
        </div>
      </Card>
      <Card title="Timed jobs (run now)">
        <p className="mb-3 text-sm text-muted">These run on schedules (scheduler and monitor every 10 minutes, sender every 2 minutes). Run them now to test without waiting.</p>
        <div className="flex flex-wrap gap-2">
          {["scheduler", "sender", "monitor"].map((j) => <Button key={j} disabled={act.busy} onClick={() => run(j)}>Run {j}</Button>)}
        </div>
        <ErrorNote error={act.error} />
        {Object.entries(results).map(([j, r]) => <pre key={j} className="mono mt-2 overflow-auto rounded bg-surface-2 p-2 text-xs">{j}: {JSON.stringify(r.result ?? r.error)}</pre>)}
      </Card>
      {releasing && (
        <Modal
          title="Release the kill switch?"
          onClose={() => setReleasing(false)}
          actions={
            <>
              <Button onClick={() => setReleasing(false)}>Cancel</Button>
              <Button tone="primary" disabled={act.busy || !ready?.every((c: any) => c.ok || !c.required)} onClick={() => act.run(async () => { await api("/settings/kill", { body: { on: false, reason } }); setReleasing(false); await refreshMe(); reload(); })}>Release</Button>
            </>
          }
        >
          <p>Live mailboxes start sending to prospects again. Before that:</p>
          <ul className="space-y-1">
            {(ready ?? []).map((c: any) => (
              <li key={c.key} className="flex items-start gap-2">
                <span className={c.ok ? "text-good" : c.required ? "text-bad" : "text-warn"}>{c.ok ? "✓" : c.required ? "✗" : "!"}</span>
                <span>{c.label}{c.note && <span className="block text-xs text-muted">{c.note}</span>}</span>
              </li>
            ))}
          </ul>
          {(ready ?? []).some((c: any) => !c.ok && !c.required) && <textarea className={inputCls} rows={2} placeholder="Reason for releasing without these (logged)" value={reason} onChange={(e) => setReason(e.target.value)} />}
          <ErrorNote error={act.error} />
        </Modal>
      )}
      {confirm && (
        <Modal title="Pause all sending?" onClose={() => setConfirm(false)} actions={<><Button onClick={() => setConfirm(false)}>Cancel</Button><Button tone="danger" onClick={() => act.run(async () => { await api("/settings/kill", { body: { on: true } }); setConfirm(false); await refreshMe(); reload(); })}>Activate kill switch</Button></>}>
          No live email will be sent from any mailbox until an admin releases it (test-mailbox sends continue). Scheduled steps stay in place and go out once resumed (if still inside their send window).
        </Modal>
      )}
    </div>
  );
}

function Template() {
  const { data, reload } = useSettings();
  const [steps, setSteps] = useState<any[] | null>(null);
  const act = useAction();
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (data) setSteps(data.settings.template); }, [data]);
  if (!steps) return <Loading />;
  const set = (i: number, k: string, v: any) => setSteps(steps.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <Card title="Sequence template" actions={<><Saved show={saved} /><Button tone="primary" disabled={act.busy} onClick={() => act.run(async () => { await api("/settings", { method: "PUT", body: { template: steps } }); setSaved(true); reload(); })}>Save changes</Button></>}>
      <p className="mb-3 text-sm text-muted">Day offsets are business days from the actual Email 1 send. Email 1 goes Tuesday–Thursday morning (never Friday); emails 2–5 thread as replies. Rep steps never block emails. Exactly 5 emails, up to 2 calls.</p>
      <Table head={["Step", "Type", "Day offset", "Owner", ""]}>
        {steps.map((s, i) => (
          <tr key={i}>
            <Td>{s.label}</Td>
            <Td>
              <select className={inputCls} value={s.type} disabled={i === 0} onChange={(e) => set(i, "type", e.target.value)}>
                <option value="email">Email</option><option value="call">Call</option><option value="linkedin_connect">LinkedIn connect</option><option value="linkedin_message">LinkedIn message</option>
              </select>
            </Td>
            <Td><input type="number" min={0} className={`${inputCls} w-24`} value={s.dayOffset} disabled={i === 0} onChange={(e) => set(i, "dayOffset", Number(e.target.value))} /></Td>
            <Td>{s.type === "email" ? "Tool" : "Rep"}</Td>
            <Td>{i > 0 && <Button tone="ghost" onClick={() => setSteps(steps.filter((_, j) => j !== i))}>Remove</Button>}</Td>
          </tr>
        ))}
      </Table>
      <Button tone="ghost" className="mt-2" onClick={() => setSteps([...steps, { type: "call", dayOffset: 5, label: "New step" }])}>+ Add step</Button>
      <ErrorNote error={act.error} />
    </Card>
  );
}

function Rules() {
  const { data, reload } = useApi<any[]>("/rules");
  const { data: users } = useApi<any[]>("/users");
  const [text, setText] = useState("");
  const [banned, setBanned] = useState("");
  const [scope, setScope] = useState("team");
  const [repSub, setRepSub] = useState("");
  const act = useAction();
  return (
    <div className="space-y-4">
      <Card title="Built-in writing rules (always on)">
        <ul className="list-disc space-y-1 pl-5 text-sm text-ink-2">
          <li>Plain text only; no tracking pixels or tracked links.</li>
          <li>Email 1: 60–100 words. Subject lines: max 7 words, no colon.</li>
          <li>No throat-clearing openers (“reaching out”, “hope this finds you”, “quick question”, “following up”).</li>
          <li>No consultant jargon (“leverage”, “streamline”, “robust”, “synergy”, “empower”, “circle back”, “deep dive”).</li>
          <li>No CRM data: currency, 3+ digit numbers, dates, pipeline codes, license claims (these <b>block</b> approval).</li>
          <li>Opt-out line and rep signature with postal address on every email.</li>
        </ul>
      </Card>
      <Card title="Team and per-rep rules">
        <ul className="mb-4 divide-y divide-line">
          {(data ?? []).filter((r) => r.status !== "proposed").map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
              <div><Badge>{r.status}</Badge> {r.text} <span className="text-xs text-muted">{r.scope === "rep" ? `rep: ${users?.find((u) => u.sub === r.repSub)?.name ?? r.repSub}` : "team"} · {r.source}</span></div>
              <Button tone="ghost" onClick={() => act.run(async () => { await api(`/rules/${r.id}/decide`, { body: { accept: r.status !== "accepted" } }); reload(); })}>{r.status === "accepted" ? "Turn off" : "Turn on"}</Button>
            </li>
          ))}
          {!(data ?? []).length && <Empty>No custom rules yet.</Empty>}
        </ul>
        <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
          <input className={inputCls} placeholder="New rule, e.g. Don't open with a question" value={text} onChange={(e) => setText(e.target.value)} />
          <select className={inputCls} value={scope} onChange={(e) => setScope(e.target.value)}><option value="team">Team-wide</option><option value="rep">One rep</option></select>
          {scope === "rep" ? <select className={inputCls} value={repSub} onChange={(e) => setRepSub(e.target.value)}><option value="">Choose rep</option>{(users ?? []).map((u) => <option key={u.sub} value={u.sub}>{u.name}</option>)}</select> : <span />}
        </div>
        <input className={`${inputCls} mt-2`} placeholder="Phrases to warn on (comma-separated, optional)" value={banned} onChange={(e) => setBanned(e.target.value)} />
        <Button className="mt-2" tone="primary" disabled={!text.trim()} onClick={() => act.run(async () => { await api("/rules", { body: { text, scope, repSub, banned: banned.split(",").map((s) => s.trim()).filter(Boolean) } }); setText(""); setBanned(""); reload(); })}>Add</Button>
        <ErrorNote error={act.error} />
      </Card>
    </div>
  );
}

function Kb() {
  const { me } = useMe();
  const { data, reload } = useApi<any[]>("/kb");
  const [edit, setEdit] = useState<any>(null);
  const act = useAction();
  const docs = data ?? [];
  return (
    <div className="space-y-4">
      <Card title="Knowledge base" actions={<Button tone="primary" onClick={() => setEdit({ offering: me.settings.offerings[0], title: "", content: KB_TEMPLATE })}>New document</Button>}>
        <p className="mb-3 text-sm text-muted">Drafting uses only <b>approved</b> documents, and drops front matter and any section headed <code>[to-confirm]</code>. Editing an approved document sends it back to draft.</p>
        <Table head={["Offering", "Document", "Version", "Status", "", ""]}>
          {me.settings.offerings.map((o) => {
            const ds = docs.filter((d) => d.offering === o);
            if (!ds.length) return <tr key={o}><Td>{o}</Td><Td className="text-muted">none</Td><Td /><Td /><Td /><Td /></tr>;
            return ds.map((d) => (
              <tr key={d.id}>
                <Td>{o}</Td>
                <Td>{d.title}<div className="mono text-xs text-muted">{d.path}</div></Td>
                <Td>v{d.version}</Td>
                <Td><Badge tone={d.status === "approved" ? "approved" : "draft"}>{d.status}</Badge>{/\[to-confirm\]/i.test(d.content) && <span className="ml-1 text-xs text-warn">[to-confirm]</span>}</Td>
                <Td><Button tone="ghost" onClick={() => setEdit(d)}>Edit</Button></Td>
                <Td>{d.status !== "approved" && <Button tone="success" onClick={() => act.run(async () => { await api(`/kb/${d.id}/approve`, { body: {} }); reload(); })}>Approve</Button>}</Td>
              </tr>
            ));
          })}
        </Table>
        <ErrorNote error={act.error} />
      </Card>
      {edit && (
        <Card title={edit.id ? `Edit ${edit.title}` : "New document"} actions={<><Button onClick={() => setEdit(null)}>Cancel</Button><Button tone="primary" onClick={() => act.run(async () => { edit.id ? await api(`/kb/${edit.id}`, { method: "PUT", body: edit }) : await api("/kb", { body: edit }); setEdit(null); reload(); })}>Save changes</Button></>}>
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="Offering"><select className={inputCls} value={edit.offering} disabled={!!edit.id} onChange={(e) => setEdit({ ...edit, offering: e.target.value })}>{me.settings.offerings.map((o) => <option key={o}>{o}</option>)}</select></Field>
            <Field label="Title"><input className={inputCls} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></Field>
          </div>
          <textarea className={`${inputCls} mono mt-2 min-h-96 text-xs`} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} />
        </Card>
      )}
    </div>
  );
}
const KB_TEMPLATE = `---
approved:
cleared_claims: []
---
# What it is

# Who it's for

# Proof points (cleared only)

# Topics and angles

# [to-confirm] Claims waiting on approval
`;

function Suppression() {
  const { data, reload } = useApi<any[]>("/suppression");
  const [address, setAddress] = useState("");
  const [reason, setReason] = useState("Rep do-not-contact");
  const act = useAction();
  return (
    <Card title={`Suppression list (${data?.length ?? 0})`}>
      <div className="mb-3 flex flex-wrap gap-2">
        <input className={`${inputCls} max-w-xs`} placeholder="email@company.com" value={address} onChange={(e) => setAddress(e.target.value)} />
        <select className={`${inputCls} w-56`} value={reason} onChange={(e) => setReason(e.target.value)}>
          {["Rep do-not-contact", "Intellias SF Email Opt Out", "Target list opt-out", "Opt-out reply"].map((r) => <option key={r}>{r}</option>)}
        </select>
        <Button tone="primary" onClick={() => act.run(async () => { await api("/suppression", { body: { address, reason } }); setAddress(""); reload(); })}>Add</Button>
      </div>
      <ErrorNote error={act.error} />
      <p className="mb-2 text-xs text-muted">Salesforce Email Opt Out imports arrive with the nightly sync (Phase 4). Opt-outs in the target list import set the contact's opt-out flag directly.</p>
      <Table head={["Address", "Reason", "Source", "Added", ""]}>
        {(data ?? []).map((s) => (
          <tr key={s.address}>
            <Td>{s.address}</Td><Td>{s.reason}</Td><Td>{s.source}</Td><Td className="text-muted">{fmtDate(s.addedAt)} {s.addedBy && `· ${s.addedBy}`}</Td>
            <Td><Button tone="ghost" onClick={() => act.run(async () => { await api(`/suppression/${encodeURIComponent(s.address)}`, { method: "DELETE" }); reload(); })}>Remove</Button></Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

function Calendar() {
  const { data, reload } = useSettings();
  const [cal, setCal] = useState<any>(null);
  const [newDay, setNewDay] = useState("");
  const [saved, setSaved] = useState(false);
  const act = useAction();
  useEffect(() => { if (data) setCal(data.settings.calendar); }, [data]);
  if (!cal) return <Loading />;
  const names = Object.fromEntries((data.federal as any[]).map((h) => [h.date, h.name]));
  const save = () => act.run(async () => { await api("/settings", { method: "PUT", body: { calendar: cal } }); setSaved(true); reload(); });
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Send window" actions={<><Saved show={saved} /><Button tone="primary" onClick={save}>Save changes</Button></>}>
        <p className="mb-3 text-sm text-muted">In each contact's own time zone (from their state; Arizona and Hawaii have no DST; split-state accounts use the majority zone).</p>
        <div className="flex gap-3">
          <Field label="Start (hour)"><input type="number" min={0} max={23} className={`${inputCls} w-24`} value={cal.windowStartHour} onChange={(e) => setCal({ ...cal, windowStartHour: Number(e.target.value) })} /></Field>
          <Field label="End (hour)"><input type="number" min={1} max={24} className={`${inputCls} w-24`} value={cal.windowEndHour} onChange={(e) => setCal({ ...cal, windowEndHour: Number(e.target.value) })} /></Field>
        </div>
        <ErrorNote error={act.error} />
      </Card>
      <Card title={`Holidays (${cal.holidays.length})`} actions={<Button tone="primary" onClick={save}>Save changes</Button>}>
        <div className="mb-3 flex gap-2">
          <input type="date" className={`${inputCls} w-48`} value={newDay} onChange={(e) => setNewDay(e.target.value)} />
          <Button disabled={!newDay} onClick={() => { setCal({ ...cal, holidays: [...new Set([...cal.holidays, newDay])].sort() }); setNewDay(""); }}>Add</Button>
        </div>
        <ul className="max-h-96 divide-y divide-line overflow-auto text-sm">
          {cal.holidays.map((d: string) => (
            <li key={d} className="flex items-center justify-between py-1">
              <span>{d} <span className="text-muted">{names[d] ?? ""}</span></span>
              <Button tone="ghost" onClick={() => setCal({ ...cal, holidays: cal.holidays.filter((x: string) => x !== d) })}>Remove</Button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function Mailboxes() {
  const { data, reload } = useApi<any[]>("/mailboxes");
  const { data: graph } = useApi<any>("/graph");
  const act = useAction();
  const [edit, setEdit] = useState<any>(null);
  const [goLive, setGoLive] = useState<any>(null);
  const [confirmText, setConfirmText] = useState("");
  const [note, setNote] = useState<Record<string, string>>({});
  const MODE: Record<string, [string, string]> = { "dry-run": ["Paused", "dry-run"], test: ["draft", "test"], live: ["active", "live"] };
  const say = (sub: string, msg: string) => setNote((n) => ({ ...n, [sub]: msg }));
  return (
    <div className="space-y-4">
      <Card title="Microsoft Graph connection">
        {!graph ? (
          <Loading />
        ) : graph.configured ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Badge tone="approved">connected</Badge>
            <span>
              Entra app <code>{graph.clientId}</code>
            </span>
            {graph.expiresOn && <span className={graph.daysToExpiry < 30 ? "font-semibold text-bad" : "text-muted"}>secret expires {graph.expiresOn} ({graph.daysToExpiry} days)</span>}
          </div>
        ) : (
          <div className="text-sm text-muted">
            Not connected. An admin runs <code>node scripts/set-graph-secret.mjs</code> after creating the "AI SDR Mail" Entra app (see docs/email-graph.md). Until then every mailbox is dry-run.
          </div>
        )}
      </Card>
      <Card title="Mailboxes">
        <p className="mb-3 text-sm text-muted">
          <b>Dry-run</b>: nothing is sent; emails appear on the Outbox page. <b>Test</b> (dry-run with a test mailbox): emails really go out through Graph, but only to the test mailbox, with the prospect's address in the subject. Reply to one to try the reply rules. <b>Live</b>: emails go to prospects from the rep's mailbox. New live mailboxes ramp from 20 to 50 a day over 10 business days. Auto-pause: 2+ bounces and over 3% of the last 50 sends, or a Graph access error.
        </p>
        <Table head={["Rep", "Sending mailbox", "Test mailbox", "Mode", "Today", "Bounce rate", "Inbox", "State", ""]}>
          {(data ?? []).map((m) => (
            <tr key={m.repSub}>
              <Td>{m.repName}</Td>
              <Td>{m.address}</Td>
              <Td className="text-muted">{m.testMailbox ?? "—"}</Td>
              <Td>
                <Badge tone={MODE[m.mode]?.[0]}>{MODE[m.mode]?.[1] ?? m.mode}</Badge>
                {m.live && m.liveSince && <div className="text-xs text-muted">since {fmtDate(m.liveSince)}</div>}
              </Td>
              <Td>
                <CapBar n={m.sentToday} cap={m.capToday} />
              </Td>
              <Td>
                <BounceRate r={m.recent?.length ? m.recent.filter((x: string) => x === "bounce").length / m.recent.length : 0} />
              </Td>
              <Td className="text-xs">
                {m.mode === "dry-run" ? <span className="text-muted">off</span> : m.inboxError ? <span className="text-bad">{m.inboxError}</span> : m.lastInboxSync ? <span className="text-muted">synced {fmtDate(m.lastInboxSync)}</span> : <span className="text-muted">waiting for first sync</span>}
              </Td>
              <Td>
                {m.paused ? <Badge tone="failed">paused</Badge> : <Badge tone="active">ok</Badge>}
                {m.pausedReason && <div className="max-w-56 text-xs text-muted">{m.pausedReason}</div>}
              </Td>
              <Td className="whitespace-nowrap">
                <div className="flex flex-wrap gap-1">
                  <Button tone="ghost" onClick={() => setEdit(m)}>
                    Edit
                  </Button>
                  <Button
                    tone="ghost"
                    disabled={!graph?.configured}
                    onClick={() =>
                      act.run(async () => {
                        const r = await api(`/mailboxes/${m.repSub}/check`, { body: {} });
                        say(m.repSub, r.mailbox.ok ? "Access OK: AI SDR can use this mailbox." : `No access: ${r.mailbox.message}`);
                      })
                    }
                  >
                    Check access
                  </Button>
                  <Button
                    tone="ghost"
                    disabled={!graph?.configured}
                    onClick={() =>
                      act.run(async () => {
                        const r = await api(`/mailboxes/${m.repSub}/test-email`, { body: {} });
                        say(m.repSub, `Test email sent from ${r.from} to ${r.to}.`);
                      })
                    }
                  >
                    Send test email
                  </Button>
                  {m.paused ? (
                    <Button tone="ghost" onClick={() => act.run(async () => { await api(`/mailboxes/${m.repSub}`, { method: "PUT", body: { paused: false, resetBounces: true } }); reload(); })}>
                      Resume
                    </Button>
                  ) : (
                    <Button tone="ghost" onClick={() => act.run(async () => { await api(`/mailboxes/${m.repSub}`, { method: "PUT", body: { paused: true } }); reload(); })}>
                      Pause
                    </Button>
                  )}
                  {m.live ? (
                    <Button tone="ghost" onClick={() => act.run(async () => { await api(`/mailboxes/${m.repSub}`, { method: "PUT", body: { live: false } }); reload(); })}>
                      Back to dry-run
                    </Button>
                  ) : (
                    <Button tone="danger" disabled={!graph?.configured} onClick={() => { setConfirmText(""); setGoLive(m); }}>
                      Go live…
                    </Button>
                  )}
                </div>
                {note[m.repSub] && <div className="mt-1 max-w-64 whitespace-normal text-xs text-muted">{note[m.repSub]}</div>}
              </Td>
            </tr>
          ))}
        </Table>
        <ErrorNote error={act.error} />
      </Card>
      {edit && (
        <Modal
          title={`Mailbox: ${edit.repName}`}
          onClose={() => setEdit(null)}
          actions={
            <>
              <Button onClick={() => setEdit(null)}>Cancel</Button>
              <Button tone="primary" onClick={() => act.run(async () => { await api(`/mailboxes/${edit.repSub}`, { method: "PUT", body: { address: edit.address, testMailbox: edit.testMailbox ?? "" } }); setEdit(null); reload(); })}>
                Save changes
              </Button>
            </>
          }
        >
          <Field label="Sending mailbox" help="Must be a member of aisdr-mailboxes@intellias.net.">
            <input className={inputCls} value={edit.address ?? ""} onChange={(e) => setEdit({ ...edit, address: e.target.value })} />
          </Field>
          <Field label="Test mailbox" help="Set this to send in test mode: every email goes here instead of to the prospect. Leave blank for pure dry-run. @intellias.net only.">
            <input className={inputCls} value={edit.testMailbox ?? ""} onChange={(e) => setEdit({ ...edit, testMailbox: e.target.value })} placeholder="you@intellias.net" />
          </Field>
          <ErrorNote error={act.error} />
        </Modal>
      )}
      {goLive && (
        <Modal
          title={`Go live: ${goLive.address}`}
          onClose={() => setGoLive(null)}
          actions={
            <>
              <Button onClick={() => setGoLive(null)}>Cancel</Button>
              <Button tone="danger" disabled={confirmText !== "SEND TO PROSPECTS" || act.busy} onClick={() => act.run(async () => { await api(`/mailboxes/${goLive.repSub}`, { method: "PUT", body: { live: true, confirm: confirmText } }); setGoLive(null); reload(); })}>
                Go live
              </Button>
            </>
          }
        >
          <p>
            From now on, approved sequences assigned to <b>{goLive.repName}</b> send <b>real emails to prospects</b> from {goLive.address}, starting at 20 a day and ramping to 50 over two weeks. Replies, bounces and out-of-office messages in this inbox stop or pause sequences automatically.
          </p>
          <p className="text-muted">AI SDR checks it can reach the mailbox before switching. You can switch back to dry-run or use the kill switch at any time.</p>
          <Field label="Type SEND TO PROSPECTS to confirm">
            <input className={inputCls} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
          </Field>
          <ErrorNote error={act.error} />
        </Modal>
      )}
    </div>
  );
}

function Guardrails() {
  const { refreshMe } = useMe();
  const { data, reload } = useSettings();
  const [g, setG] = useState<any>(null);
  const [offerings, setOfferings] = useState("");
  const [postal, setPostal] = useState("");
  const [saved, setSaved] = useState(false);
  const act = useAction();
  useEffect(() => { if (data) { setG(data.settings.guardrails); setOfferings(data.settings.offerings.join("\n")); setPostal(data.settings.postalAddress); } }, [data]);
  if (!g) return <Loading />;
  return (
    <Card title="Guardrails, offerings and signature" actions={<><Saved show={saved} /><Button tone="primary" onClick={() => act.run(async () => { await api("/settings", { method: "PUT", body: { guardrails: g, offerings: offerings.split("\n"), postalAddress: postal } }); setSaved(true); await refreshMe(); reload(); })}>Save changes</Button></>}>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={g.usOnly} onChange={(e) => setG({ ...g, usOnly: e.target.checked })} /> Drop contacts outside the US (phase 1)</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={g.requireVerifiedEmail} onChange={(e) => setG({ ...g, requireVerifiedEmail: e.target.checked })} /> Drop unverified emails (turn on once email verification runs, Phase 3)</label>
          <Field label="Cooldown (days since last touch)"><input type="number" className={`${inputCls} w-28`} value={g.cooldownDays} onChange={(e) => setG({ ...g, cooldownDays: Number(e.target.value) })} /></Field>
          <Field label="Postal address in the signature"><input className={inputCls} value={postal} onChange={(e) => setPostal(e.target.value)} /></Field>
        </div>
        <Field label="Offerings (one per line)"><textarea className={`${inputCls} min-h-72`} value={offerings} onChange={(e) => setOfferings(e.target.value)} /></Field>
      </div>
      <ErrorNote error={act.error} />
    </Card>
  );
}

function Ai() {
  const { data, reload } = useSettings();
  const [ai, setAi] = useState<any>(null);
  const [learn, setLearn] = useState(25);
  const [saved, setSaved] = useState(false);
  const act = useAction();
  useEffect(() => { if (data) { setAi(data.settings.ai); setLearn(data.settings.learning.editsPerProposal); } }, [data]);
  if (!ai) return <Loading />;
  const u = data.usage;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Claude provider">
        <div className="space-y-2 text-sm text-ink-2">
          <div><Badge tone="approved">Amazon Bedrock</Badge> in <b>{data.bedrockRegion}</b>, called with the app's own AWS role. There's no API key to manage, and usage is billed on the qbiz AWS invoice.</div>
          <p className="text-muted">Model access is managed in the AWS console (Bedrock → Model access). If the drafting model isn't enabled yet, drafting falls back automatically: Sonnet 5.5 → Sonnet 5, Haiku 4.5 → Sonnet 5. The model actually used appears on each draft.</p>
          <p className="text-muted">Claude never receives names or email addresses. Requests stay inside AWS.</p>
        </div>
      </Card>
      <Card title="Models and spend" actions={<><Saved show={saved} /><Button tone="primary" onClick={() => act.run(async () => { await api("/settings", { method: "PUT", body: { ai, learning: { editsPerProposal: learn } } }); setSaved(true); reload(); })}>Save changes</Button></>}>
        <div className="space-y-3">
          <Field label="Drafting and learning model"><input className={inputCls} value={ai.draftModel} onChange={(e) => setAi({ ...ai, draftModel: e.target.value })} /></Field>
          <Field label="Filters and reply classification model"><input className={inputCls} value={ai.fastModel} onChange={(e) => setAi({ ...ai, fastModel: e.target.value })} /></Field>
          <Field label="Monthly limit (US$)" help="Calls stop for the rest of the month once reached."><input type="number" className={`${inputCls} w-32`} value={ai.monthlyLimitUsd} onChange={(e) => setAi({ ...ai, monthlyLimitUsd: Number(e.target.value) })} /></Field>
          <Field label="Propose rules every N edits"><input type="number" min={5} className={`${inputCls} w-32`} value={learn} onChange={(e) => setLearn(Number(e.target.value))} /></Field>
          <div className="rounded-lg bg-surface-2 p-3 text-sm">
            {u.month}: <b>${u.costUsd.toFixed(2)}</b> of ${ai.monthlyLimitUsd} · {u.calls} calls · {u.inputTokens.toLocaleString()} in / {u.outputTokens.toLocaleString()} out tokens
            <div className="mt-2 h-2 rounded bg-line"><div className={cx("h-2 rounded", u.costUsd / ai.monthlyLimitUsd > 0.8 ? "bg-bad-soft0" : "bg-accent")} style={{ width: `${Math.min(100, (u.costUsd / Math.max(ai.monthlyLimitUsd, 0.01)) * 100)}%` }} /></div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Users() {
  const { data, reload } = useApi<any[]>("/users");
  const { data: terr } = useApi<any>("/territories");
  const [edit, setEdit] = useState<any>(null);
  const act = useAction();
  const territoryOf = (email: string) => (terr?.territories ?? []).find((t: any) => (t.ownerEmail ?? "") === email.toLowerCase());
  return (
    <Card title="Users">
      <p className="mb-3 text-sm text-muted">People with an @intellias.net address can register and get the rep role. Admins change roles here. Name, title and phone appear in the email signature. Territories are assigned in the Territories tab.</p>
      <Table head={["Name", "Email", "Role", "Title", "Phone", "Territory", "Last seen", ""]}>
        {(data ?? []).map((u) => {
          const t = territoryOf(u.email);
          return (
            <tr key={u.sub}>
              <Td>{u.name}</Td>
              <Td>{u.email}</Td>
              <Td><Badge tone={u.role === "admin" ? "Enrolled" : "Paused"}>{u.role}</Badge></Td>
              <Td>{u.title ?? <span className="text-warn">not set</span>}</Td>
              <Td>{u.phone ?? <span className="text-warn">not set</span>}</Td>
              <Td className="text-xs">{t ? `${t.ownerName}: ${t.states.length} states` : <span className="text-muted">none</span>}</Td>
              <Td className="text-muted">{fmtDate(u.lastSeen)}</Td>
              <Td><Button tone="ghost" onClick={() => setEdit(u)}>Edit</Button></Td>
            </tr>
          );
        })}
      </Table>
      {edit && (
        <Modal title={`Edit ${edit.email}`} onClose={() => setEdit(null)} actions={<><Button onClick={() => setEdit(null)}>Cancel</Button><Button tone="primary" disabled={act.busy} onClick={() => act.run(async () => { await api(`/users/${edit.sub}`, { method: "PUT", body: { name: edit.name, title: edit.title, phone: edit.phone, role: edit.role } }); setEdit(null); reload(); })}>Save changes</Button></>}>
          <Field label="Name (signature)"><input className={inputCls} value={edit.name ?? ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
          <Field label="Title (signature)"><input className={inputCls} value={edit.title ?? ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })} placeholder="Sr. Director, Business Development" /></Field>
          <Field label="Phone (signature)"><input className={inputCls} value={edit.phone ?? ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} placeholder="(312) 555-0100" /></Field>
          <Field label="Role" help="Admins see Settings and Learning and can switch mailboxes to live.">
            <select className={inputCls} value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}>
              <option value="rep">Rep</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <ErrorNote error={act.error} />
        </Modal>
      )}
    </Card>
  );
}

function Territories() {
  const { data, reload } = useApi<any>("/territories");
  const { data: users } = useApi<any[]>("/users");
  const { data: unassigned, reload: reloadUnassigned } = useApi<any[]>("/unassigned");
  const [rows, setRows] = useState<any[] | null>(null);
  const [states, setStates] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const act = useAction();
  useEffect(() => {
    if (data) setRows(data.territories.map((t: any) => ({ id: t.id, ownerName: t.ownerName, ownerEmail: t.ownerEmail ?? "", states: t.states.join(", "), contacts: t.contacts })));
  }, [data]);
  if (!rows) return <Loading />;
  const set = (i: number, k: string, v: string) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-4">
      <Card
        title="Territories"
        actions={
          <>
            <Saved show={saved} />
            <Button tone="primary" disabled={act.busy} onClick={() => act.run(async () => { await api("/territories", { method: "PUT", body: { territories: rows.map((r) => ({ id: r.id, ownerName: r.ownerName, ownerEmail: r.ownerEmail, states: String(r.states).split(/[\s,]+/).filter(Boolean) })) } }); setSaved(true); reload(); reloadUnassigned(); })}>Save changes</Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-muted">Each state or province belongs to one rep. A contact's own state decides the territory (the target list's BDR owner column is Infor's and isn't used). Link a territory to the rep's account so campaigns, Contacts and Today default to it and notices reach them.</p>
        <Table head={["Owner", "Linked account", "States / provinces", "Contacts", ""]}>
          {rows.map((r, i) => (
            <tr key={r.id}>
              <Td><input className={`${inputCls} w-40`} value={r.ownerName} onChange={(e) => set(i, "ownerName", e.target.value)} /></Td>
              <Td>
                <select className={`${inputCls} w-56`} value={r.ownerEmail} onChange={(e) => set(i, "ownerEmail", e.target.value)}>
                  <option value="">Not linked yet</option>
                  {(users ?? []).map((u) => <option key={u.sub} value={u.email.toLowerCase()}>{u.name} ({u.email})</option>)}
                  {r.ownerEmail && !(users ?? []).some((u) => u.email.toLowerCase() === r.ownerEmail) && <option value={r.ownerEmail}>{r.ownerEmail} (no account yet)</option>}
                </select>
              </Td>
              <Td><textarea className={`${inputCls} min-w-72`} rows={2} value={r.states} onChange={(e) => set(i, "states", e.target.value)} /></Td>
              <Td className="tabular-nums">{r.contacts ?? 0}</Td>
              <Td><Button tone="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</Button></Td>
            </tr>
          ))}
        </Table>
        <Button tone="ghost" className="mt-2" onClick={() => setRows([...rows, { id: "", ownerName: "", ownerEmail: "", states: "", contacts: 0 }])}>+ Add territory</Button>
        <ErrorNote error={act.error} />
      </Card>
      <Card title={`Unassigned contacts (${unassigned?.length ?? 0})`}>
        <p className="mb-3 text-sm text-muted">No state, or a state outside every territory. These are never enrolled. Set the contact's state, or add the state to a territory.</p>
        {!unassigned?.length ? (
          <Empty>Every contact has a territory.</Empty>
        ) : (
          <Table head={["Contact", "Account", "Why", "Set state", ""]}>
            {unassigned.slice(0, 200).map((c: any) => (
              <tr key={c.id}>
                <Td><Link className="text-accent-text hover:underline" to={`/contacts/${c.id}`}>{c.name}</Link><div className="text-xs text-muted">{c.title}</div></Td>
                <Td>{c.accountName}{c.accountState && <div className="text-xs text-muted">account state {c.accountState}</div>}</Td>
                <Td className="text-xs">{c.reason}</Td>
                <Td><input className={`${inputCls} w-24`} placeholder={c.accountState ?? "IL"} value={states[c.id] ?? ""} onChange={(e) => setStates({ ...states, [c.id]: e.target.value })} /></Td>
                <Td><Button tone="ghost" disabled={!states[c.id]} onClick={() => act.run(async () => { await api(`/contacts/${c.id}/state`, { method: "PUT", body: { state: states[c.id] } }); reloadUnassigned(); reload(); })}>Save</Button></Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

function Import() {
  const { data, reload } = useApi<any[]>("/imports");
  const [file, setFile] = useState<File | null>(null);
  const act = useAction();
  useEffect(() => {
    if (!(data ?? []).some((i) => i.status === "uploading" || i.status === "processing")) return;
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [data, reload]);
  const upload = () =>
    act.run(async () => {
      const r = await api("/imports", { body: { filename: file!.name, contentType: file!.type || "application/octet-stream" } });
      const put = await fetch(r.url, { method: "PUT", body: file, headers: { "content-type": file!.type || "application/octet-stream" } });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      setFile(null);
      reload();
    });
  return (
    <Card title="Import target list">
      <p className="mb-3 text-sm text-muted">Upload the Infor Outreach Console export (.xlsx with Accounts / Contacts / Signals sheets, or a .csv of contacts with account columns). Contacts match on email, then alternate emails; accounts on website domain, then name. Opt-outs and Do Not Call only ever turn on.</p>
      <div className="flex flex-wrap items-center gap-2">
        <input type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <Button tone="primary" disabled={!file || act.busy} onClick={upload}>{act.busy ? "Uploading…" : "Upload and import"}</Button>
      </div>
      <ErrorNote error={act.error} />
      <div className="mt-4">
        <Table head={["File", "By", "Status", "Result"]}>
          {(data ?? []).map((i) => (
            <tr key={i.key}>
              <Td>{i.filename}<div className="text-xs text-muted">{fmtDate(i.at)}</div></Td>
              <Td>{i.by}</Td>
              <Td><Badge tone={i.status === "done" ? "approved" : i.status === "failed" ? "failed" : "queued"}>{i.status}</Badge></Td>
              <Td className="text-xs">{i.result ? `${i.result.accountsCreated} new / ${i.result.accountsUpdated} updated accounts · ${i.result.contactsCreated} new / ${i.result.contactsUpdated} updated contacts · ${i.result.signals} signals · ${i.result.skippedCount} skipped (sheets: ${i.result.sheets.join(", ")})` : i.error}</Td>
            </tr>
          ))}
        </Table>
      </div>
    </Card>
  );
}

function Simulate() {
  const [kind, setKind] = useState("reply");
  const [fromEmail, setFrom] = useState("");
  const [text, setText] = useState("");
  const [target, setTarget] = useState("");
  const [oppName, setOpp] = useState("");
  const [returnDate, setReturn] = useState("");
  const [result, setResult] = useState<any>(null);
  const act = useAction();
  const samples: Record<string, string> = {
    reply: "Thanks for the note. We're actually looking at this for next quarter, can you send some times next week?",
    opt: "Please remove me from your list.",
    ooo: "I'm out of the office with limited access to email and will return on Monday.",
  };
  return (
    <Card title="Simulate inbound (testing)">
      <p className="mb-3 text-sm text-muted">Feeds the real inbox handler until Microsoft Graph polling is connected: replies stop the sequence and pause the account, opt-outs suppress and queue a Salesforce opt-out, bounces stop and count toward the mailbox auto-pause, out-of-office pauses and shifts every remaining step.</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Type">
          <select className={inputCls} value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="reply">Reply (Claude classifies it)</option><option value="ooo">Out of office</option><option value="bounce">Bounce</option><option value="new_opportunity">New open opportunity in Salesforce</option>
          </select>
        </Field>
        {kind === "new_opportunity" ? (
          <>
            <Field label="Account ID" help="From the account page URL"><input className={inputCls} value={target} onChange={(e) => setTarget(e.target.value)} /></Field>
            <Field label="Opportunity name"><input className={inputCls} value={oppName} onChange={(e) => setOpp(e.target.value)} /></Field>
          </>
        ) : kind === "bounce" ? (
          <Field label="Bounced address"><input className={inputCls} value={target} onChange={(e) => setTarget(e.target.value)} /></Field>
        ) : (
          <Field label="From address" help="A contact's email, or anyone at a target account's domain"><input className={inputCls} value={fromEmail} onChange={(e) => setFrom(e.target.value)} /></Field>
        )}
        {kind === "ooo" && <Field label="Return date (optional)" help="Blank: parsed from the text, else 5 business days"><input type="date" className={inputCls} value={returnDate} onChange={(e) => setReturn(e.target.value)} /></Field>}
      </div>
      {kind !== "new_opportunity" && (
        <>
          <textarea className={`${inputCls} mt-3`} rows={4} placeholder="Message text" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="mt-1 flex gap-2 text-xs">
            {Object.entries(samples).map(([k, v]) => <button key={k} className="text-accent-text hover:underline" onClick={() => setText(v)}>sample {k}</button>)}
          </div>
        </>
      )}
      <Button className="mt-3" tone="primary" disabled={act.busy} onClick={() => act.run(async () => setResult(await api("/simulate", { body: { kind, fromEmail, text, target, oppName, returnDate: returnDate || undefined } })))}>Process</Button>
      <ErrorNote error={act.error} />
      {result && <pre className="mono mt-3 overflow-auto rounded bg-surface-2 p-2 text-xs">{JSON.stringify(result, null, 2)}</pre>}
    </Card>
  );
}

function Audit() {
  const { data, loading } = useApi<any[]>("/audit");
  const [open, setOpen] = useState<string | null>(null);
  if (loading && !data) return <Loading />;
  return (
    <Card title="Audit log (latest 400)">
      <Table head={["When", "Actor", "Action", "Entity", ""]}>
        {(data ?? []).map((a) => (
          <tr key={a.id}>
            <Td className="whitespace-nowrap text-muted">{fmtDate(a.at)}</Td>
            <Td className="text-xs">{a.actor}</Td>
            <Td className="mono text-xs">{a.action}</Td>
            <Td className="mono text-xs">{a.entityType}:{a.entityId}</Td>
            <Td>
              {(a.before || a.after) && <Button tone="ghost" onClick={() => setOpen(open === a.id ? null : a.id)}>{open === a.id ? "Hide" : "Details"}</Button>}
              {open === a.id && <pre className="mono mt-1 max-w-xl overflow-auto whitespace-pre-wrap text-xs">{JSON.stringify({ before: a.before, after: a.after }, null, 2)}</pre>}
            </Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}
